import { useCallback, useEffect, useMemo, useState } from "react";
import PageTitle from "../../../components/ui/PageTitle";
import LoadingIndicator from "../../../components/ui/LoadingIndicator";
import ErrorMessage from "../../../components/ui/ErrorMessage";
import Button from "../../../components/ui/Button";
import {
  getUsersList,
  getMyClientConfig,
  updateMyClientConfig,
} from "../../../lib/apiClient";
import { UserProfile } from "../../../types";
import { getErrorMessage } from "../../../lib/utils";
import { ROLES } from "../../../lib/constants";
import toast from "react-hot-toast";

type IntegrationFlags = {
  telegramEnabled: boolean;
  discordEnabled: boolean;
  slackEnabled: boolean;
};

type ComplianceFlags = {
  kycEnabled: boolean;
  agreementSigningEnabled: boolean;
};

const DEFAULT_FLAGS: IntegrationFlags = {
  telegramEnabled: true,
  discordEnabled: true,
  slackEnabled: true,
};

const DEFAULT_COMPLIANCE: ComplianceFlags = {
  kycEnabled: false,
  agreementSigningEnabled: false,
};

export default function SuperAdminIntegrationsPage() {
  const [admins, setAdmins] = useState<UserProfile[]>([]);
  const [selectedAdminId, setSelectedAdminId] = useState<string>("");
  const [flags, setFlags] = useState<IntegrationFlags>(DEFAULT_FLAGS);
  const [compliance, setCompliance] = useState<ComplianceFlags>(DEFAULT_COMPLIANCE);
  const [isLoadingAdmins, setIsLoadingAdmins] = useState(true);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedAdmin = useMemo(
    () => admins.find((admin) => admin._id === selectedAdminId) || null,
    [admins, selectedAdminId]
  );

  const loadAdmins = useCallback(async () => {
    setIsLoadingAdmins(true);
    setError(null);
    try {
      const { users } = await getUsersList({ role: ROLES.ADMIN, page: 1, limit: 200 });
      const adminUsers = users.filter(
        (user) =>
          (typeof user.role_id === "object" && user.role_id?.name === ROLES.ADMIN) ||
          user.role === ROLES.ADMIN
      );
      setAdmins(adminUsers);
      if (adminUsers.length > 0) setSelectedAdminId(adminUsers[0]._id);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      toast.error(`Failed to load admin accounts: ${message}`, { id: "integrations-admin-load-error" });
    } finally {
      setIsLoadingAdmins(false);
    }
  }, []);

  const loadAdminIntegrationConfig = useCallback(async (adminId: string) => {
    if (!adminId) return;
    setIsLoadingConfig(true);
    setError(null);
    try {
      const config = await getMyClientConfig(adminId);
      const featureFlags = config?.config?.platform?.features || {};
      const complianceConfig = (config?.config as any)?.compliance || {};
      setFlags({
        telegramEnabled: featureFlags.telegramEnabled !== false,
        discordEnabled: featureFlags.discordEnabled !== false,
        slackEnabled: featureFlags.slackEnabled !== false,
      });
      setCompliance({
        kycEnabled: complianceConfig.kycEnabled === true,
        agreementSigningEnabled: complianceConfig.agreementSigningEnabled === true,
      });
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      toast.error(`Failed to load tenant settings: ${message}`, { id: "integrations-config-load-error" });
    } finally {
      setIsLoadingConfig(false);
    }
  }, []);

  useEffect(() => { loadAdmins(); }, [loadAdmins]);
  useEffect(() => { if (selectedAdminId) loadAdminIntegrationConfig(selectedAdminId); }, [selectedAdminId, loadAdminIntegrationConfig]);

  const handleToggle = (key: keyof IntegrationFlags) =>
    setFlags((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleComplianceToggle = (key: keyof ComplianceFlags) =>
    setCompliance((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSave = async () => {
    if (!selectedAdminId) return;
    setIsSaving(true);
    setError(null);
    try {
      await updateMyClientConfig(
        {
          platform: {
            features: {
              telegramEnabled: flags.telegramEnabled,
              discordEnabled: flags.discordEnabled,
              slackEnabled: flags.slackEnabled,
            },
          },
          compliance: {
            kycEnabled: compliance.kycEnabled,
            agreementSigningEnabled: compliance.agreementSigningEnabled,
          },
        },
        selectedAdminId
      );
      toast.success("Admin settings updated successfully.", { id: "integrations-save-success" });
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      toast.error(`Failed to update settings: ${message}`, { id: "integrations-save-error" });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingAdmins) {
    return (
      <div className="space-y-6">
        <PageTitle title="Admin Permissions" subtitle="Control platform access and compliance settings per admin" />
        <LoadingIndicator text="Loading admin accounts..." size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Admin Permissions"
        subtitle="Enable/disable platforms and compliance (KYC, agreements) per admin tenant"
      />

      {error && <ErrorMessage title="Unable to Continue" message={error} />}

      <div className="bg-gradient-to-br from-dark-secondary/80 to-dark-tertiary/60 backdrop-blur-xl rounded-xl border border-dark-border/50 p-5 md:p-6 space-y-6">
        {/* Admin selector */}
        <div className="space-y-2">
          <label className="block text-sm text-text-secondary">Admin Account</label>
          <select
            className="w-full bg-dark-primary border border-dark-border rounded-lg px-3 py-2 text-text-primary"
            value={selectedAdminId}
            onChange={(e) => setSelectedAdminId(e.target.value)}
            disabled={admins.length === 0 || isLoadingConfig || isSaving}
          >
            {admins.length === 0 ? (
              <option value="">No admin accounts found</option>
            ) : (
              admins.map((admin) => (
                <option key={admin._id} value={admin._id}>
                  {admin.name || admin.loginId || admin.phone || admin._id}
                </option>
              ))
            )}
          </select>
        </div>

        {selectedAdmin && (
          <>
            {isLoadingConfig ? (
              <LoadingIndicator text="Loading settings..." />
            ) : (
              <>
                {/* Platform toggles */}
                <div className="space-y-3">
                  <h2 className="text-base font-semibold text-text-primary">
                    Platform Access for{" "}
                    <span className="text-golden-accent">
                      {selectedAdmin.name || selectedAdmin.loginId || "Admin"}
                    </span>
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <IntegrationToggleCard
                      label="Telegram"
                      description="Allow Telegram channel creation and management"
                      enabled={flags.telegramEnabled}
                      onToggle={() => handleToggle("telegramEnabled")}
                    />
                    <IntegrationToggleCard
                      label="Discord"
                      description="Allow Discord guild and role management"
                      enabled={flags.discordEnabled}
                      onToggle={() => handleToggle("discordEnabled")}
                    />
                    <IntegrationToggleCard
                      label="Slack"
                      description="Allow Slack workspace channel management"
                      enabled={flags.slackEnabled}
                      onToggle={() => handleToggle("slackEnabled")}
                    />
                  </div>
                </div>

                {/* Compliance toggles */}
                <div className="space-y-3 border-t border-dark-border/30 pt-5">
                  <div>
                    <h2 className="text-base font-semibold text-text-primary">Compliance Settings</h2>
                    <p className="text-xs text-text-secondary mt-0.5">
                      These are disabled by default. Enabling them adds KYC/agreement steps to the subscriber flow.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <IntegrationToggleCard
                      label="KYC Required"
                      description="Subscribers must complete KYC before platform access is granted"
                      enabled={compliance.kycEnabled}
                      onToggle={() => handleComplianceToggle("kycEnabled")}
                    />
                    <IntegrationToggleCard
                      label="Agreement Signing"
                      description="Subscribers must digitally sign an agreement via Digio before access"
                      enabled={compliance.agreementSigningEnabled}
                      onToggle={() => handleComplianceToggle("agreementSigningEnabled")}
                    />
                  </div>
                </div>
              </>
            )}
          </>
        )}

        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            isLoading={isSaving}
            disabled={!selectedAdminId || isLoadingConfig || isSaving}
          >
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
}

function IntegrationToggleCard({
  label,
  description,
  enabled,
  onToggle,
}: {
  label: string;
  description: string;
  enabled: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="rounded-xl border border-dark-border/60 bg-dark-primary/60 p-4 space-y-3">
      <div>
        <h3 className="text-base font-semibold text-text-primary">{label}</h3>
        <p className="text-xs text-text-secondary">{description}</p>
      </div>
      <button
        type="button"
        onClick={onToggle}
        className={`w-full py-2 rounded-lg border text-sm font-semibold transition-colors ${
          enabled
            ? "border-functional-success/50 bg-functional-success/15 text-functional-success"
            : "border-functional-danger/50 bg-functional-danger/15 text-functional-danger"
        }`}
      >
        {enabled ? "Enabled" : "Disabled"}
      </button>
    </div>
  );
}
